/**
 * FamilyTreeCanvas Component
 *
 * Main canvas area for rendering the family tree visualization.
 * Displays person nodes and connection lines with a subtle grid background.
 *
 * Features:
 * - Pedigree-specific layout (paternal LEFT, maternal RIGHT)
 * - Pan and zoom support
 * - Interactive node selection
 * - Viewport culling for large trees (scalable to 1000+ nodes)
 */

import { useMemo, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonNode } from './PersonNode';
import { computeTreeLayout, determineTreeRoot, type UnionNode } from '../utils/genealogyLayoutEngine';
import { performViewportCulling, shouldEnableCulling, getCullingRecommendation } from '../utils/viewportCulling';
import { computeLayoutAsync } from '../services/layoutService';
import type { PersonNodeCanvas, RelationshipEdge } from '@/types/tree-dashboard.types';
import type { Couple } from '@/types/entities';
import type { UseCanvasTransformResult } from '../hooks/useCanvasTransform';
import { hasParentOfGender, type FamilyTreeGraphContext } from '../validation';
import { dateService } from '@/services/DateService';
import { useTreeViewSettings, UNLIMITED_DEPTH } from '@/store/treeViewSettings';

interface FamilyTreeCanvasProps {
  persons: PersonNodeCanvas[];
  relationships: RelationshipEdge[];
  couples: Couple[];  // Phase 3 Slice 4: Couple data for union visualization
  selectedPersonId?: string;
  treeOwnerId?: string; // Added for determining root person
  treeName: string;
  totalMembers: number;
  totalGenerations: number;
  onPersonSelect: (personId: string) => void;
  canvasTransform: UseCanvasTransformResult;
  onAddParent?: (childId: string, parentType: 'father' | 'mother') => void;
  onAddRelative?: (personId: string) => void; // Opens relationship chooser modal
  onLayoutChange?: (positions: Map<string, { x: number; y: number }>) => void; // For search centering
}

/**
 * Get edge styling based on relationship subtype (Phase 2 - Slice 3)
 *
 * Enhanced visibility for adoptive relationships:
 * - biological or null: solid stroke (default, backward compatible)
 * - adoptive: thicker dashed stroke with distinct color
 * - foster: dotted stroke
 */
function getEdgeStyle(subtype?: 'biological' | 'adoptive' | 'foster' | null): {
  strokeDasharray?: string;
  strokeWidth?: string;
  className?: string;
} {
  if (subtype === 'adoptive') {
    return {
      strokeDasharray: '10 6', // Longer dashes for better visibility
      strokeWidth: '3', // Thicker line
      className: 'stroke-amber-500 dark:stroke-amber-600', // Distinct amber color
    };
  }

  if (subtype === 'foster') {
    return {
      strokeDasharray: '4 2', // Dotted pattern
      strokeWidth: '2',
      className: 'stroke-slate-400 dark:stroke-slate-600',
    };
  }

  // Default: solid stroke for biological or undefined (backward compatible)
  return {
    strokeWidth: '2',
    className: 'stroke-slate-300 dark:stroke-slate-700',
  };
}

/**
 * Render SVG connection line - supports both simple and T-junction patterns
 */
function renderConnection(
  conn: {
    fromX?: number;
    fromY?: number;
    toX?: number;
    toY?: number;
    pathData?: string;
    junctionX?: number;
    junctionY?: number;
    type: 'parent-child' | 'spouse';
    relationship_subtype?: 'biological' | 'adoptive' | 'foster' | null;
  }
) {
  if (conn.type === 'spouse') {
    // ENHANCED: Spouse connection with visual clarity improvements
    // Thicker line with distinct styling to clearly show marriage/partnership
    const midX = ((conn.fromX ?? 0) + (conn.toX ?? 0)) / 2;
    const midY = ((conn.fromY ?? 0) + (conn.toY ?? 0)) / 2;

    return (
      <g>
        {/* Main spouse connection line - thicker and more prominent */}
        <line
          x1={conn.fromX}
          y1={conn.fromY}
          x2={conn.toX}
          y2={conn.toY}
          stroke="#fb7185"  // Use direct color instead of className
          strokeWidth="3"
          opacity="0.8"
        />
        {/* Double line effect for emphasis */}
        <line
          x1={conn.fromX}
          y1={(conn.fromY ?? 0) + 3}
          x2={conn.toX}
          y2={(conn.toY ?? 0) + 3}
          stroke="#fda4af"  // Use direct color
          strokeWidth="1.5"
          opacity="0.5"
        />
        {/* Union symbol at midpoint - small heart or circle */}
        <circle
          cx={midX}
          cy={midY}
          r="8"
          fill="#ffe4e6"  // Use direct color
          stroke="#fb7185"  // Use direct color
          strokeWidth="2"
          opacity="0.9"
        />
        {/* Heart symbol in the center */}
        <text
          x={midX}
          y={midY + 3}
          textAnchor="middle"
          fill="#f43f5e"  // Use direct color
          fontSize="10"
          fontFamily="sans-serif"
        >
          ♥
        </text>
      </g>
    );
  }

  // Get edge styling based on relationship subtype (Phase 2 - Slice 3)
  const edgeStyle = getEdgeStyle(conn.relationship_subtype);

  // T-junction path for couple → children (genealogy pattern)
  if (conn.pathData) {
    return (
      <g>
        <path
          d={conn.pathData}
          className={edgeStyle.className || "stroke-slate-300 dark:stroke-slate-700"}
          strokeWidth={edgeStyle.strokeWidth || "2"}
          strokeDasharray={edgeStyle.strokeDasharray}
          fill="none"
        />
        {/* Junction dot at T-junction point */}
        {conn.junctionX !== undefined && conn.junctionY !== undefined && (
          <circle
            cx={conn.junctionX}
            cy={conn.junctionY}
            r="4"
            className={edgeStyle.className || "fill-slate-300 dark:fill-slate-700"}
          />
        )}
      </g>
    );
  }

  // Simple parent-child line (single parent) - L-shaped path
  const midY = ((conn.fromY ?? 0) + (conn.toY ?? 0)) / 2;
  const path = `M ${conn.fromX} ${conn.fromY} L ${conn.fromX} ${midY} L ${conn.toX} ${midY} L ${conn.toX} ${conn.toY}`;

  return (
    <g>
      <path
        d={path}
        className={edgeStyle.className || "stroke-slate-300 dark:stroke-slate-700"}
        strokeWidth={edgeStyle.strokeWidth || "2"}
        strokeDasharray={edgeStyle.strokeDasharray}
        fill="none"
      />
      {/* Junction dot at midpoint */}
      <circle
        cx={conn.fromX}
        cy={midY}
        r="4"
        className={edgeStyle.className || "fill-slate-300 dark:fill-slate-700"}
      />
    </g>
  );
}

/**
 * Render couple box with marriage metadata (Phase 3 Slice 4)
 * Draws a subtle frame around spouse pairs with date range below
 * Enhanced with remarriage order indicators
 *
 * Phase 3 Checkpoint 2: Reduced metadata at low zoom for clarity
 *
 * @param union - Union node with couple metadata
 * @param spouse1Node - Left spouse positioned node
 * @param spouse2Node - Right spouse positioned node
 * @param key - Unique key for React rendering
 * @param marriageOrder - Order of this marriage (1st, 2nd, 3rd, etc.) for remarriage indication
 * @param currentZoom - Current zoom scale for adaptive rendering
 */
function renderCoupleBox(
  union: UnionNode,
  spouse1Node: { x: number; y: number } | undefined,
  spouse2Node: { x: number; y: number } | undefined,
  key: string,
  marriageOrder?: number,
  currentZoom?: number
) {
  // Skip if union doesn't have both spouses positioned
  if (!spouse1Node || !spouse2Node) return null;

  // Skip if no couple metadata available (backward compatible)
  if (!union.couple_id) return null;

  // Phase 3 Checkpoint 2: Hide date labels at very low zoom (< 0.5) to reduce clutter
  const MIN_ZOOM_FOR_DATE_LABELS = 0.5;
  const showDateLabels = !currentZoom || currentZoom >= MIN_ZOOM_FOR_DATE_LABELS;

  const NODE_WIDTH = 224;
  const NODE_HEIGHT = 80;

  // Calculate box dimensions to encompass both spouse nodes
  const leftSpouse = spouse1Node.x < spouse2Node.x ? spouse1Node : spouse2Node;
  const rightSpouse = spouse1Node.x < spouse2Node.x ? spouse2Node : spouse1Node;

  const boxPadding = 12;
  const boxX = leftSpouse.x - boxPadding;
  const boxY = leftSpouse.y - boxPadding;
  const boxWidth = (rightSpouse.x + NODE_WIDTH) - leftSpouse.x + (boxPadding * 2);
  const boxHeight = NODE_HEIGHT + (boxPadding * 2);

  // Format marriage date range
  const dateRange = dateService.formatMarriageDateRange(
    union.date_start,
    union.date_end,
    union.has_ended
  );

  // Remarriage indicator styling
  const isRemarriage = marriageOrder && marriageOrder > 1;
  const boxColor = isRemarriage
    ? "stroke-blue-400 dark:stroke-blue-600"
    : "stroke-slate-300 dark:stroke-slate-700";
  const boxStrokeWidth = isRemarriage ? "2" : "1.5";

  return (
    <g key={key}>
      {/* Couple frame/box */}
      <rect
        x={boxX}
        y={boxY}
        width={boxWidth}
        height={boxHeight}
        className={boxColor}
        strokeWidth={boxStrokeWidth}
        fill="none"
        rx="10" // Rounded corners
      />

      {/* Remarriage order indicator (top-left corner badge) */}
      {isRemarriage && (
        <text
          x={boxX + 8}
          y={boxY + 14}
          className="fill-blue-600 dark:fill-blue-400 font-bold text-[10px]"
          style={{ fontFamily: 'system-ui, sans-serif' }}
        >
          {marriageOrder === 2 ? '2nd' : marriageOrder === 3 ? '3rd' : `${marriageOrder}th`}
        </text>
      )}

      {/* Marriage date range (below box, if available) */}
      {/* Phase 3 Checkpoint 3: Enhanced visibility with chip/badge style */}
      {dateRange && showDateLabels && (
        <g>
          {/* Background chip for better visibility */}
          <rect
            x={boxX + boxWidth / 2 - (dateRange.length * 3.5) - 8}
            y={boxY + boxHeight + 6}
            width={dateRange.length * 7 + 16}
            height={20}
            rx="10"
            className="fill-slate-100 dark:fill-slate-800"
            opacity="0.95"
          />
          {/* Date text on chip */}
          <text
            x={boxX + boxWidth / 2}
            y={boxY + boxHeight + 19}
            textAnchor="middle"
            className="fill-slate-700 dark:fill-slate-300 text-[11px] font-medium"
            style={{ fontFamily: 'system-ui, sans-serif' }}
          >
            {dateRange}
          </text>
        </g>
      )}
    </g>
  );
}

export function FamilyTreeCanvas({
  persons,
  relationships,
  couples,
  selectedPersonId,
  treeOwnerId,
  treeName,
  totalMembers,
  totalGenerations,
  onPersonSelect,
  canvasTransform,
  onAddParent,
  onAddRelative,
  onLayoutChange,
}: FamilyTreeCanvasProps) {
  const { t } = useTranslation('common');

  // Control variable to hide/show Add Father and Add Mother buttons
  // Set to false to hide these buttons and only use "Add Relatives"
  const DISABLE_ADD_PARENT_BUTTONS = true;

  // Phase 3 Slice 8: Get depth preferences from store
  const { ancestorDepth, descendantDepth, setAncestorDepth, setDescendantDepth } = useTreeViewSettings();

  // Collapsible utility panel state (mobile/tablet only)
  const [isUtilityPanelExpanded, setIsUtilityPanelExpanded] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine if we should show collapsible behavior (mobile/tablet only)
  const showCollapsibleBehavior = windowWidth < 1024;

  // Calculate actual max generations in the FULL tree for depth limit validation
  // This must be calculated from the full tree, not the filtered layout
  const actualMaxGenerations = useMemo(() => {
    if (persons.length === 0) return { ancestors: 0, descendants: 0 };

    // Determine the fixed root person for the unified tree
    const treeRootId = determineTreeRoot(persons, relationships, treeOwnerId);
    if (!treeRootId) return { ancestors: 0, descendants: 0 };

    // Compute full layout without depth limits to get actual generation range
    const fullLayout = computeTreeLayout(
      persons,
      relationships,
      treeRootId,
      couples,
      { maxAncestorDepth: UNLIMITED_DEPTH, maxDescendantDepth: UNLIMITED_DEPTH }
    );

    if (fullLayout.nodes.length === 0) return { ancestors: 0, descendants: 0 };

    // Extract generation values from full layout
    const generations = fullLayout.nodes.map(n => (n as any).generation || 0);
    const minGen = Math.min(...generations);
    const maxGen = Math.max(...generations);

    return {
      ancestors: Math.abs(minGen), // Ancestors are negative generations
      descendants: maxGen,
    };
  }, [persons, relationships, couples, treeOwnerId]);

  // Phase 3 Slice 9: Layout computation state management
  // Track layout computation status and result
  const [layout, setLayout] = useState<{
    nodes: any[];
    connections: any[];
    unions: UnionNode[];
    bounds: { minX: number; maxX: number; minY: number; maxY: number; width: number; height: number };
  }>({ nodes: [], connections: [], unions: [], bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 } });

  const [isComputingLayout, setIsComputingLayout] = useState(false);

  // CRITICAL FIX: Compute layout based on FIXED tree root, not selected person
  // This creates a unified family tree that does NOT rebuild on selection
  // Phase 3 Slice 8: Layout respects depth limits from store
  // Phase 3 Slice 9: Layout computed asynchronously using Web Worker
  useEffect(() => {
    if (persons.length === 0) {
      setLayout({ nodes: [], connections: [], unions: [], bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 } });
      return;
    }

    // Determine the fixed root person for the unified tree
    const treeRootId = determineTreeRoot(persons, relationships, treeOwnerId);

    console.log('[FamilyTreeCanvas] Computing GENEALOGY-based tree layout...', {
      personsCount: persons.length,
      relationshipsCount: relationships.length,
      couplesCount: couples.length,
      treeRootId,
      treeRootName: persons.find(p => p.id === treeRootId)?.first_name,
      selectedPersonId, // Logged but NOT used for layout computation
      depthLimits: {
        ancestors: ancestorDepth === UNLIMITED_DEPTH ? 'unlimited' : ancestorDepth,
        descendants: descendantDepth === UNLIMITED_DEPTH ? 'unlimited' : descendantDepth,
      },
    });

    // DEBUG: Check relationship types in the data
    const spouseRelationships = relationships.filter(r => r.relationship_type === 'spouse');
    console.log('[DEBUG] Spouse relationships in data:', spouseRelationships.length, spouseRelationships);

    // Mark computation as in progress
    setIsComputingLayout(true);

    // Phase 3 Slice 4: Pass real couples data into layout engine
    // Phase 3 Slice 8: Pass depth limits to layout engine
    // Phase 3 Slice 9: Use async Web Worker computation
    // Layout is computed with fixed root - will NOT change when selectedPersonId changes
    computeLayoutAsync(
      persons,
      relationships,
      treeRootId || undefined,
      couples,
      {
        maxAncestorDepth: ancestorDepth,
        maxDescendantDepth: descendantDepth,
      }
    )
      .then(computedLayout => {
        console.log('[FamilyTreeCanvas] Layout computed:', {
          nodesCount: computedLayout.nodes.length,
          connectionsCount: computedLayout.connections.length,
          bounds: computedLayout.bounds,
        });

        // DEBUG: Check connection types in the computed layout
        const spouseConnections = computedLayout.connections.filter((c: any) => c.type === 'spouse');
        console.log('[DEBUG] Spouse connections in layout:', spouseConnections.length, spouseConnections);

        setLayout(computedLayout);
        setIsComputingLayout(false);

        // Call onLayoutChange callback with person positions for search centering
        if (onLayoutChange && computedLayout.nodes.length > 0) {
          const positions = new Map<string, { x: number; y: number }>();
          computedLayout.nodes.forEach((node: any) => {
            positions.set(node.person.id, { x: node.x, y: node.y });
          });
          onLayoutChange(positions);
        }
      })
      .catch(error => {
        console.error('[FamilyTreeCanvas] Layout computation failed:', error);

        // Fallback to empty layout on error
        setLayout({ nodes: [], connections: [], unions: [], bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 } });
        setIsComputingLayout(false);
      });
  }, [persons, relationships, couples, treeOwnerId, ancestorDepth, descendantDepth]); // Phase 3 Slice 8: Added depth dependencies

  // Canvas container reference for measuring dimensions
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });

  // Measure canvas container dimensions
  useEffect(() => {
    if (!canvasContainerRef.current) return;

    const updateDimensions = () => {
      if (canvasContainerRef.current) {
        const rect = canvasContainerRef.current.getBoundingClientRect();
        setCanvasDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    // Initial measurement
    updateDimensions();

    // Update on resize
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(canvasContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Viewport culling - compute visible nodes and connections
  const { visibleNodes, visibleConnections, cullingStats } = useMemo(() => {
    const enableCulling = shouldEnableCulling(layout.nodes.length);

    if (!enableCulling || canvasDimensions.width === 0 || canvasDimensions.height === 0) {
      // Culling disabled or canvas not yet measured - render all
      return {
        visibleNodes: layout.nodes,
        visibleConnections: layout.connections,
        cullingStats: {
          totalNodes: layout.nodes.length,
          visibleNodes: layout.nodes.length,
          culledNodes: 0,
          totalConnections: layout.connections.length,
          visibleConnections: layout.connections.length,
          culledConnections: 0,
          cullingEnabled: false,
        },
      };
    }

    // Perform viewport culling
    const result = performViewportCulling(
      layout.nodes,
      layout.connections,
      canvasDimensions.width,
      canvasDimensions.height,
      canvasTransform.transform
    );

    return {
      visibleNodes: result.visibleNodes,
      visibleConnections: result.visibleConnections,
      cullingStats: {
        ...result.stats,
        cullingEnabled: true,
      },
    };
  }, [layout.nodes, layout.connections, canvasDimensions.width, canvasDimensions.height, canvasTransform.transform]);

  // Log culling performance (only when culling is active)
  useEffect(() => {
    if (cullingStats.cullingEnabled && cullingStats.culledNodes > 0) {
      console.log('[FamilyTreeCanvas] Viewport culling active:', {
        recommendation: getCullingRecommendation(layout.nodes.length),
        stats: cullingStats,
      });
    }
  }, [cullingStats, layout.nodes.length]);

  // Helper: Get parent IDs for a person
  const getParentIds = (personId: string): string[] => {
    return relationships
      .filter(rel => rel.relationship_type === 'parent_child' && rel.person_b_id === personId)
      .map(rel => rel.person_a_id);
  };

  // Helper: Calculate parent couple union center for Add Parent button positioning
  const calculateParentUnionPosition = (childId: string): { x: number; y: number } | null => {
    const parentIds = getParentIds(childId);
    const childNode = layout.nodes.find(n => n.person.id === childId);
    if (!childNode) return null;

    const NODE_WIDTH = 224;
    const GENERATION_GAP = 250; // Vertical spacing between generations

    if (parentIds.length === 0) {
      // No parents - position buttons one generation above the child
      return {
        x: childNode.x + NODE_WIDTH / 2, // Center above child
        y: childNode.y - GENERATION_GAP, // One generation up
      };
    } else if (parentIds.length === 1) {
      // One parent exists - position the missing parent button beside the existing parent
      const existingParent = layout.nodes.find(n => n.person.id === parentIds[0]);
      if (!existingParent) {
        // Fallback: position above child if parent node not found
        return {
          x: childNode.x + NODE_WIDTH / 2,
          y: childNode.y - GENERATION_GAP,
        };
      }

      // Position at the same level as the existing parent
      return {
        x: existingParent.x + NODE_WIDTH / 2, // Center horizontally with parent
        y: existingParent.y, // Same vertical position as parent
      };
    } else {
      // Two parents - use couple union center (midpoint between them)
      const parent1 = layout.nodes.find(n => n.person.id === parentIds[0]);
      const parent2 = layout.nodes.find(n => n.person.id === parentIds[1]);

      if (!parent1 || !parent2) {
        // Fallback: position above child if parent nodes not found
        return {
          x: childNode.x + NODE_WIDTH / 2,
          y: childNode.y - GENERATION_GAP,
        };
      }

      // Calculate union center (midpoint between the couple)
      return {
        x: (parent1.x + parent2.x + NODE_WIDTH) / 2,
        y: (parent1.y + parent2.y) / 2,
      };
    }
  };

  const {
    transform,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    wheelHandlerRef,
    isDragging,
    isPinching,
    fitToView, // Phase 3 Checkpoint 3
    resetTransform, // Phase 3 Checkpoint 3
  } = canvasTransform;

  // Phase 3 Checkpoint 2: Low-zoom visual clutter reduction
  // Hide interactive controls (Add Parent/Relative buttons) when zoomed out
  const MIN_ZOOM_FOR_CONTROLS = 0.7; // Hide controls below 70% zoom
  const showInteractiveControls = transform.scale >= MIN_ZOOM_FOR_CONTROLS;

  // Build graph context for validation
  const graphContext: FamilyTreeGraphContext = useMemo(() => ({
    persons,
    relationships,
  }), [persons, relationships]);

  // Attach native wheel event listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container || !wheelHandlerRef.current) return;

    const wheelHandler = wheelHandlerRef.current;
    container.addEventListener('wheel', wheelHandler, { passive: false });

    return () => {
      container.removeEventListener('wheel', wheelHandler);
    };
  }, [wheelHandlerRef]);

  // CRITICAL iOS Safari Fix: Native touch event listeners with passive: false
  // React synthetic events are not reliable enough on iOS Safari
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;

    // Native touch handlers that properly work on iOS Safari
    const handleNativeTouchStart = (e: TouchEvent) => {
      // Check if touching interactive elements - if so, allow normal behavior
      const target = e.target as HTMLElement;
      if (
        target.closest('.person-node') ||
        target.closest('.person-node-container') ||
        target.closest('[role="button"]') ||
        target.closest('button') ||
        target.closest('.pointer-events-auto') // Any interactive element
      ) {
        // Allow normal click behavior on interactive elements
        return;
      }

      // Only prevent default for canvas dragging, not for interactive elements
      e.preventDefault();

      // Call the React handler with synthetic event
      const syntheticEvent = {
        touches: e.touches,
        preventDefault: () => e.preventDefault(),
        target: e.target,
        currentTarget: e.currentTarget
      } as unknown as React.TouchEvent;

      handleTouchStart(syntheticEvent);
    };

    const handleNativeTouchMove = (e: TouchEvent) => {
      // Check if we're on an interactive element
      const target = e.target as HTMLElement;
      if (
        target.closest('.person-node') ||
        target.closest('.person-node-container') ||
        target.closest('[role="button"]') ||
        target.closest('button') ||
        target.closest('.pointer-events-auto')
      ) {
        return; // Don't prevent default on interactive elements
      }

      // Only prevent default if we're actually dragging/pinching
      if (isDragging || isPinching) {
        e.preventDefault();
      }

      const syntheticEvent = {
        touches: e.touches,
        preventDefault: () => e.preventDefault(),
        currentTarget: e.currentTarget
      } as unknown as React.TouchEvent;

      handleTouchMove(syntheticEvent);
    };

    const handleNativeTouchEnd = (e: TouchEvent) => {
      // Check if we're ending on an interactive element
      const target = e.target as HTMLElement;
      if (
        target.closest('.person-node') ||
        target.closest('.person-node-container') ||
        target.closest('[role="button"]') ||
        target.closest('button') ||
        target.closest('.pointer-events-auto')
      ) {
        // Allow normal click to happen on interactive elements
        // Don't preventDefault here
      } else if (isDragging || isPinching) {
        // Only prevent default if we were dragging/pinching
        e.preventDefault();
      }

      const syntheticEvent = {
        touches: e.touches,
        preventDefault: () => e.preventDefault()
      } as unknown as React.TouchEvent;

      handleTouchEnd(syntheticEvent);
    };

    // Add native listeners with passive: false for iOS Safari compatibility
    container.addEventListener('touchstart', handleNativeTouchStart, { passive: false });
    container.addEventListener('touchmove', handleNativeTouchMove, { passive: false });
    container.addEventListener('touchend', handleNativeTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleNativeTouchStart);
      container.removeEventListener('touchmove', handleNativeTouchMove);
      container.removeEventListener('touchend', handleNativeTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, isDragging, isPinching]);

  return (
    <div
      ref={canvasContainerRef}
      className="flex-1 relative bg-slate-50 dark:bg-[#0b0d14] overflow-hidden ios-touch-fix"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      // React touch handlers - let native handlers control preventDefault
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        cursor: isDragging ? 'grabbing' : isPinching ? 'zoom-in' : 'grab',
        // CRITICAL iOS Safari fixes
        touchAction: 'none', // Disable ALL touch actions
        userSelect: 'none', // Prevent text selection
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none', // Disable iOS callout
        overscrollBehavior: 'none',
        WebkitOverflowScrolling: 'auto', // Disable momentum scrolling
        // Prevent any transform or will-change issues
        willChange: 'auto',
        transform: 'translateZ(0)', // Force GPU layer
        WebkitTransform: 'translateZ(0)'
      }}
    >
      {/* Background Grid (Subtle) */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(#1e3fae 1px, transparent 0)',
          backgroundSize: '40px 40px',
          backgroundPosition: `${transform.x}px ${transform.y}px`,
          transform: `scale(${transform.scale})`,
          transformOrigin: '0 0',
        }}
      />

      {/* Transformed Canvas Content */}
      <div
        className="absolute inset-0"
        style={{
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          transformOrigin: '0 0',
        }}
      >
        {/* SVG Lines (Connection lines between generations) */}
        <svg
          className="absolute pointer-events-none"
          style={{
            left: '50%',
            top: '50%',
            overflow: 'visible',
          }}
        >
          <g>
            {/* Connection lines */}
            {visibleConnections.map(conn => (
              <g key={conn.id}>
                {renderConnection(conn)}
              </g>
            ))}

            {/* Phase 3 Slice 4: Couple boxes with marriage metadata */}
            {layout.unions.map(union => {
              // Find spouse nodes for this union
              const spouse1 = layout.nodes.find(n => n.person.id === union.spouse1Id);
              const spouse2 = layout.nodes.find(n => n.person.id === union.spouse2Id);

              // Calculate marriage order for remarriage indication
              // Find all unions involving either spouse, sorted by date_start
              const person1Unions = layout.unions.filter(u =>
                (u.spouse1Id === union.spouse1Id || u.spouse2Id === union.spouse1Id) && u.couple_id
              ).sort((a, b) => {
                if (!a.date_start) return 1;
                if (!b.date_start) return -1;
                return a.date_start.localeCompare(b.date_start);
              });

              const person2Unions = layout.unions.filter(u =>
                (u.spouse1Id === union.spouse2Id || u.spouse2Id === union.spouse2Id) && u.couple_id
              ).sort((a, b) => {
                if (!a.date_start) return 1;
                if (!b.date_start) return -1;
                return a.date_start.localeCompare(b.date_start);
              });

              // Marriage order is the maximum order number from either spouse's perspective
              const spouse1Order = person1Unions.findIndex(u => u.id === union.id) + 1;
              const spouse2Order = person2Unions.findIndex(u => u.id === union.id) + 1;
              const marriageOrder = Math.max(spouse1Order, spouse2Order);

              return renderCoupleBox(union, spouse1, spouse2, `couple-box-${union.id}`, marriageOrder > 0 ? marriageOrder : undefined, transform.scale);
            })}
          </g>
        </svg>

        {/* Tree Nodes */}
        <div
          className="absolute"
          style={{
            left: '50%',
            top: '50%',
          }}
        >
          {visibleNodes.map(node => {
            // Use validation layer to check parent relationships
            const hasFather = hasParentOfGender(graphContext, node.person.id, 'male');
            const hasMother = hasParentOfGender(graphContext, node.person.id, 'female');
            // Phase 3 Checkpoint 2: Hide add controls at low zoom to reduce visual clutter
            // Also check DISABLE_ADD_PARENT_BUTTONS to hide Add Father/Mother buttons when disabled
            const showAddParents = !DISABLE_ADD_PARENT_BUTTONS && showInteractiveControls && onAddParent && (!hasFather || !hasMother);
            const showAddRelative = showInteractiveControls && onAddRelative;

            // Calculate couple union position for Add Parent buttons
            const unionPos = calculateParentUnionPosition(node.person.id);

            return (
              <div
                key={node.person.id}
                className="absolute person-node-container"
                style={{
                  left: `${node.x}px`,
                  top: `${node.y}px`,
                }}
              >
                {/* Add Parent Buttons (Directly above person card) */}
                {showAddParents && (
                  <div className="absolute left-1/2 -translate-x-1/2 flex gap-3 pointer-events-auto z-10" style={{ top: '-90px' }}>
                    {!hasFather && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddParent(node.person.id, 'father');
                        }}
                        className="group flex flex-col items-center gap-1 px-3 py-2 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg bg-blue-50/80 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-400 dark:hover:border-blue-600 transition-all duration-200"
                        title={t('tree.canvas.addButtons.addFather')}
                      >
                        <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-lg">
                          add
                        </span>
                        <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 whitespace-nowrap">
                          {t('tree.canvas.addButtons.addFather')}
                        </span>
                      </button>
                    )}
                    {!hasMother && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddParent(node.person.id, 'mother');
                        }}
                        className="group flex flex-col items-center gap-1 px-3 py-2 border-2 border-dashed border-pink-300 dark:border-pink-700 rounded-lg bg-pink-50/80 dark:bg-pink-900/20 hover:bg-pink-100 dark:hover:bg-pink-900/40 hover:border-pink-400 dark:hover:border-pink-600 transition-all duration-200"
                        title={t('tree.canvas.addButtons.addMother')}
                      >
                        <span className="material-symbols-outlined text-pink-600 dark:text-pink-400 text-lg">
                          add
                        </span>
                        <span className="text-xs font-semibold text-pink-700 dark:text-pink-300 whitespace-nowrap">
                          {t('tree.canvas.addButtons.addMother')}
                        </span>
                      </button>
                    )}
                  </div>
                )}

                {/* Person Card */}
                <PersonNode
                  person={{
                    id: node.person.id,
                    first_name: node.person.first_name,
                    last_name: node.person.last_name,
                    gender: node.person.gender,
                    birth_date: node.person.date_of_birth,
                    death_date: node.person.date_of_death,
                    profile_image_url: node.person.signedImageUrl,
                  }}
                  isSelected={node.person.id === selectedPersonId}
                  onClick={() => onPersonSelect(node.person.id)}
                />

                {/* Add Relative Button (Below the card) */}
                {showAddRelative && (
                  <div className="absolute top-[calc(100%+16px)] left-1/2 -translate-x-1/2 pointer-events-auto z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddRelative(node.person.id);
                      }}
                      className="group flex flex-col items-center gap-1 px-3 py-2 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50/80 dark:bg-slate-900/20 hover:bg-slate-100 dark:hover:bg-slate-900/40 hover:border-slate-400 dark:hover:border-slate-600 transition-all duration-200"
                      title={t('tree.canvas.addButtons.addRelative')}
                    >
                      <span className="material-symbols-outlined text-slate-600 dark:text-slate-400 text-lg">
                        add
                      </span>
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                        {t('tree.canvas.addButtons.addRelative')}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Dashboard Utility Controls - Bottom Left - Responsive per breakpoint */}
      <div className="absolute bottom-16 md:bottom-20 lg:bottom-24 left-3 md:left-4 lg:left-6 flex flex-col gap-2 md:gap-2.5 lg:gap-3 pointer-events-none z-30 max-w-[calc(100vw-1.5rem)] md:max-w-[280px] lg:max-w-sm">
        {/* Collapse/Expand Toggle (Mobile/Tablet Only) */}
        {showCollapsibleBehavior && (
          <button
            onClick={() => setIsUtilityPanelExpanded(!isUtilityPanelExpanded)}
            className="self-start bg-white/95 dark:bg-slate-900/95 p-2 rounded-lg border border-slate-200 dark:border-slate-800 shadow-lg pointer-events-auto backdrop-blur-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label={isUtilityPanelExpanded ? "Collapse controls" : "Expand controls"}
            title={isUtilityPanelExpanded ? "Collapse controls" : "Expand controls"}
          >
            <span className="material-symbols-outlined text-lg text-slate-700 dark:text-slate-300">
              {isUtilityPanelExpanded ? 'expand_more' : 'expand_less'}
            </span>
          </button>
        )}

        {/* Utility Controls - Collapsible on mobile/tablet, always visible on desktop */}
        {(!showCollapsibleBehavior || isUtilityPanelExpanded) && (
          <>
            {/* Tree Info Card - Ultra-compact on mobile, medium on tablet, full on desktop */}
            <div className="bg-white/95 dark:bg-slate-900/95 p-2 md:p-2.5 lg:p-3 rounded-lg md:rounded-xl border border-slate-200 dark:border-slate-800 shadow-lg pointer-events-auto backdrop-blur-sm">
              <div className="flex items-center gap-2 md:gap-2.5">
                <div className="size-8 md:size-9 lg:size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                  <span className="material-symbols-outlined text-lg md:text-xl lg:text-2xl">family_history</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] md:text-xs lg:text-sm font-bold truncate">{treeName}</p>
                  <p className="text-[9px] md:text-[10px] lg:text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 md:gap-1.5 mt-0.5">
                    <span className="font-semibold">{totalMembers}</span>
                    <span className="hidden md:inline">{t('tree.canvas.info.members')}</span>
                    <span className="md:hidden">mbr</span>
                    <span className="text-slate-400">•</span>
                    <span className="font-semibold">{totalGenerations}</span>
                    <span className="hidden md:inline">{t('tree.canvas.info.generations')}</span>
                    <span className="md:hidden">gen</span>
                  </p>
                </div>
              </div>
            </div>

            {/* View Controls + Depth Controls - More compact on mobile/tablet */}
            <div className="bg-white/95 dark:bg-slate-900/95 p-2 md:p-2.5 lg:p-3 rounded-lg md:rounded-xl border border-slate-200 dark:border-slate-800 shadow-lg pointer-events-auto backdrop-blur-sm">
          <div className="flex flex-col gap-2 md:gap-2.5 lg:gap-3">
            {/* View Control Buttons - More compact on mobile */}
            <div className="flex gap-1.5 md:gap-2">
              <button
                onClick={() => fitToView(layout.bounds, canvasDimensions.width, canvasDimensions.height)}
                className="flex-1 flex items-center justify-center gap-1 md:gap-1.5 px-2 md:px-2.5 lg:px-3 py-1.5 md:py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-md md:rounded-lg transition-colors"
                title="Fit tree to view"
              >
                <span className="material-symbols-outlined text-sm md:text-base lg:text-lg">fit_screen</span>
                <span className="text-[10px] md:text-xs lg:text-sm font-semibold hidden md:inline">Fit</span>
              </button>
              <button
                onClick={resetTransform}
                className="flex-1 flex items-center justify-center gap-1 md:gap-1.5 px-2 md:px-2.5 lg:px-3 py-1.5 md:py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md md:rounded-lg transition-colors"
                title="Reset view"
              >
                <span className="material-symbols-outlined text-sm md:text-base lg:text-lg text-slate-600 dark:text-slate-400">refresh</span>
                <span className="text-[10px] md:text-xs lg:text-sm font-semibold text-slate-700 dark:text-slate-300 hidden md:inline">Reset</span>
              </button>
            </div>

            {/* Divider */}
            <div className="h-px bg-slate-200 dark:bg-slate-700"></div>

            {/* Depth Controls - Ultra-compact on mobile */}
            <div className="flex flex-col gap-1.5 md:gap-2">
              <div className="text-[9px] md:text-[10px] lg:text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                Tree Depth
              </div>

              {/* Ancestor Depth Control - Ultra-compact on mobile */}
              <div className="flex items-center justify-between gap-1.5 md:gap-2">
                <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                  <span className="material-symbols-outlined text-xs md:text-sm text-blue-600 dark:text-blue-400">arrow_upward</span>
                  <span className="text-[10px] md:text-xs font-medium text-slate-700 dark:text-slate-300 w-10 md:w-16">
                    <span className="hidden lg:inline">Ancestors</span>
                    <span className="lg:hidden">Anc</span>
                  </span>
                </div>
                <div className="flex items-center gap-1 md:gap-1.5 bg-slate-100 dark:bg-slate-800 rounded-md md:rounded-lg p-0.5 md:p-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newDepth = ancestorDepth === UNLIMITED_DEPTH
                        ? Math.max(actualMaxGenerations.ancestors, 5)
                        : Math.max(0, ancestorDepth - 1);
                      console.log('[FamilyTreeCanvas] Ancestor depth decreased:', newDepth);
                      setAncestorDepth(newDepth);
                    }}
                    disabled={ancestorDepth === 0}
                    className="size-6 md:size-7 lg:size-8 flex items-center justify-center rounded bg-white dark:bg-slate-900 hover:bg-blue-50 dark:hover:bg-blue-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Decrease ancestor depth"
                    aria-label="Decrease ancestor depth"
                  >
                    <span className="material-symbols-outlined text-sm md:text-base text-slate-700 dark:text-slate-300">remove</span>
                  </button>
                  <div className="min-w-[32px] md:min-w-[40px] text-center">
                    <span className="text-xs md:text-sm font-bold text-slate-800 dark:text-slate-200">
                      {ancestorDepth === UNLIMITED_DEPTH ? 'All' : ancestorDepth}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const isAtMax = ancestorDepth >= actualMaxGenerations.ancestors && actualMaxGenerations.ancestors > 0;
                      const newDepth = isAtMax ? UNLIMITED_DEPTH : (ancestorDepth === UNLIMITED_DEPTH ? actualMaxGenerations.ancestors : ancestorDepth + 1);
                      console.log('[FamilyTreeCanvas] Ancestor depth increased:', newDepth, 'max:', actualMaxGenerations.ancestors);
                      setAncestorDepth(newDepth);
                    }}
                    disabled={ancestorDepth === UNLIMITED_DEPTH}
                    className="size-6 md:size-7 lg:size-8 flex items-center justify-center rounded bg-white dark:bg-slate-900 hover:bg-blue-50 dark:hover:bg-blue-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Increase ancestor depth"
                    aria-label="Increase ancestor depth"
                  >
                    <span className="material-symbols-outlined text-sm md:text-base text-slate-700 dark:text-slate-300">add</span>
                  </button>
                </div>
              </div>

              {/* Descendant Depth Control - Ultra-compact on mobile */}
              <div className="flex items-center justify-between gap-1.5 md:gap-2">
                <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                  <span className="material-symbols-outlined text-xs md:text-sm text-green-600 dark:text-green-400">arrow_downward</span>
                  <span className="text-[10px] md:text-xs font-medium text-slate-700 dark:text-slate-300 w-10 md:w-16">
                    <span className="hidden lg:inline">Descendants</span>
                    <span className="lg:hidden">Desc</span>
                  </span>
                </div>
                <div className="flex items-center gap-1 md:gap-1.5 bg-slate-100 dark:bg-slate-800 rounded-md md:rounded-lg p-0.5 md:p-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newDepth = descendantDepth === UNLIMITED_DEPTH
                        ? Math.max(actualMaxGenerations.descendants, 5)
                        : Math.max(0, descendantDepth - 1);
                      console.log('[FamilyTreeCanvas] Descendant depth decreased:', newDepth);
                      setDescendantDepth(newDepth);
                    }}
                    disabled={descendantDepth === 0}
                    className="size-6 md:size-7 lg:size-8 flex items-center justify-center rounded bg-white dark:bg-slate-900 hover:bg-green-50 dark:hover:bg-green-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Decrease descendant depth"
                    aria-label="Decrease descendant depth"
                  >
                    <span className="material-symbols-outlined text-sm md:text-base text-slate-700 dark:text-slate-300">remove</span>
                  </button>
                  <div className="min-w-[32px] md:min-w-[40px] text-center">
                    <span className="text-xs md:text-sm font-bold text-slate-800 dark:text-slate-200">
                      {descendantDepth === UNLIMITED_DEPTH ? 'All' : descendantDepth}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const isAtMax = descendantDepth >= actualMaxGenerations.descendants && actualMaxGenerations.descendants > 0;
                      const newDepth = isAtMax ? UNLIMITED_DEPTH : (descendantDepth === UNLIMITED_DEPTH ? actualMaxGenerations.descendants : descendantDepth + 1);
                      console.log('[FamilyTreeCanvas] Descendant depth increased:', newDepth, 'max:', actualMaxGenerations.descendants);
                      setDescendantDepth(newDepth);
                    }}
                    disabled={descendantDepth === UNLIMITED_DEPTH}
                    className="size-6 md:size-7 lg:size-8 flex items-center justify-center rounded bg-white dark:bg-slate-900 hover:bg-green-50 dark:hover:bg-green-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Increase descendant depth"
                    aria-label="Increase descendant depth"
                  >
                    <span className="material-symbols-outlined text-sm md:text-base text-slate-700 dark:text-slate-300">add</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
          </>
        )}

        {/* Viewport Culling Stats (Debug Overlay) - Hidden on mobile */}
        {cullingStats.cullingEnabled && (
          <div className="hidden lg:block bg-emerald-50/95 dark:bg-emerald-950/95 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800 shadow-xl max-w-xs pointer-events-auto">
            <div className="flex items-start gap-2">
              <div className="size-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                <span className="material-symbols-outlined text-sm">visibility</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-emerald-900 dark:text-emerald-100 mb-1">
                  {t('tree.canvas.culling.title')}
                </p>
                <div className="text-[9px] text-emerald-700 dark:text-emerald-300 space-y-0.5">
                  <p>
                    {t('tree.canvas.culling.nodes')}: <span className="font-semibold">{cullingStats.visibleNodes}</span>/{cullingStats.totalNodes} {t('tree.canvas.culling.visible')}
                    {cullingStats.culledNodes > 0 && (
                      <span className="text-emerald-600 dark:text-emerald-400">
                        {' '}({Math.round((cullingStats.culledNodes / cullingStats.totalNodes) * 100)}% {t('tree.canvas.culling.culled')})
                      </span>
                    )}
                  </p>
                  <p>
                    {t('tree.canvas.culling.connections')}: <span className="font-semibold">{cullingStats.visibleConnections}</span>/{cullingStats.totalConnections} {t('tree.canvas.culling.visible')}
                    {cullingStats.culledConnections > 0 && (
                      <span className="text-emerald-600 dark:text-emerald-400">
                        {' '}({Math.round((cullingStats.culledConnections / cullingStats.totalConnections) * 100)}% {t('tree.canvas.culling.culled')})
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pinch Zoom Indicator - Shows when actively pinching */}
      {isPinching && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2 pointer-events-none z-50 animate-in fade-in duration-200">
          <span className="material-symbols-outlined text-sm">pinch_zoom_in</span>
          {Math.round(transform.scale * 100)}%
        </div>
      )}

      {/* Phase 3 Slice 9: Loading State (Lightweight) */}
      {isComputingLayout && layout.nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <span className="material-symbols-outlined text-3xl text-primary">family_history</span>
            </div>
            <h3 className="text-lg font-bold mb-2">Computing tree layout...</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Building the family tree visualization
            </p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isComputingLayout && layout.nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-3xl text-primary">family_history</span>
            </div>
            <h3 className="text-lg font-bold mb-2">{t('tree.canvas.emptyState.title')}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('tree.canvas.emptyState.description')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
